import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { AppShell } from "@/components/AppShell";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { formatMoney } from "@/lib/constants";
import {
  Megaphone, Gavel, Trophy, Wallet, TrendingUp, Clock, Users, ArrowRight, Compass, PlusCircle,
} from "lucide-react";

const ACCENTS = {
  "neon-lime": "bg-neon-lime/10 border-neon-lime/20 text-neon-lime",
  "neon-cyan": "bg-neon-cyan/10 border-neon-cyan/20 text-neon-cyan",
  "neon-pink": "bg-neon-pink/10 border-neon-pink/20 text-neon-pink",
  "neon-purple": "bg-neon-purple/10 border-neon-purple/20 text-neon-purple",
};

function StatCard({ icon: Icon, label, value, accent = "neon-lime", i = 0 }) {
  return (
    <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.06 }}
      className="rounded-2xl border border-border bg-surface p-6">
      <div className={`h-10 w-10 rounded-xl border flex items-center justify-center mb-4 ${ACCENTS[accent]}`}>
        <Icon className="h-5 w-5" />
      </div>
      <p className="text-3xl font-heading font-black tracking-tight">{value}</p>
      <p className="text-sm text-muted-foreground mt-1">{label}</p>
    </motion.div>
  );
}

const statusColors = {
  open: "bg-neon-cyan/15 text-neon-cyan border-neon-cyan/30",
  awarded: "bg-neon-lime/15 text-neon-lime border-neon-lime/30",
  closed: "bg-muted text-muted-foreground border-border",
  pending: "bg-neon-cyan/15 text-neon-cyan border-neon-cyan/30",
  accepted: "bg-neon-lime/15 text-neon-lime border-neon-lime/30",
  rejected: "bg-destructive/15 text-destructive border-destructive/30",
};

function PromoterDashboard({ user }) {
  const [stats, setStats] = useState(null);
  const [campaigns, setCampaigns] = useState([]);

  useEffect(() => {
    api.get("/stats/promoter").then((r) => setStats(r.data));
    api.get("/campaigns/mine").then((r) => setCampaigns(r.data));
  }, []);

  return (
    <div className="max-w-6xl mx-auto">
      <div className="mb-8">
        <p className="text-xs font-bold uppercase tracking-[0.2em] text-neon-pink mb-2">Promoter dashboard</p>
        <h1 className="font-heading font-black text-4xl tracking-tight">Welcome back, {user.name?.split(" ")[0]} 👋</h1>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-10">
        <StatCard icon={Megaphone} label="Total campaigns" value={stats?.total_campaigns ?? "—"} i={0} />
        <StatCard icon={Clock} label="Open for bids" value={stats?.open_campaigns ?? "—"} accent="neon-cyan" i={1} />
        <StatCard icon={Gavel} label="Bids received" value={stats?.total_bids ?? "—"} accent="neon-pink" i={2} />
        <StatCard icon={Wallet} label="Total committed" value={stats ? formatMoney(stats.total_spend, stats.currency) : "—"} accent="neon-purple" i={3} />
      </div>

      <div className="flex items-center justify-between mb-5">
        <h2 className="font-heading font-bold text-2xl">Your campaigns</h2>
        <Link to="/campaigns/new"><Button data-testid="post-campaign-btn" className="rounded-full bg-neon-lime text-background hover:bg-neon-lime/90 font-semibold"><PlusCircle className="h-4 w-4 mr-1" /> Post Campaign</Button></Link>
      </div>

      {campaigns.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-border bg-surface p-12 text-center">
          <Megaphone className="h-10 w-10 text-muted-foreground mx-auto mb-4" />
          <p className="font-heading font-bold text-xl mb-2">No campaigns yet</p>
          <p className="text-muted-foreground mb-6">Post your first requirement and let creators bid for it.</p>
          <Link to="/campaigns/new"><Button className="rounded-full bg-neon-lime text-background hover:bg-neon-lime/90 font-semibold">Create your first campaign</Button></Link>
        </div>
      ) : (
        <div className="grid gap-4">
          {campaigns.map((c) => (
            <Link key={c.campaign_id} to={`/campaigns/${c.campaign_id}`} data-testid={`campaign-card-${c.campaign_id}`}
              className="group rounded-2xl border border-border bg-surface p-6 hover:bg-surfaceHover transition-colors flex items-center justify-between gap-4">
              <div className="min-w-0">
                <div className="flex items-center gap-3 mb-1.5">
                  <Badge variant="outline" className={`capitalize ${statusColors[c.status]}`}>{c.status}</Badge>
                  {c.category && <span className="text-xs text-muted-foreground">{c.category}</span>}
                </div>
                <h3 className="font-heading font-bold text-lg truncate group-hover:text-neon-lime transition-colors">{c.title}</h3>
                <p className="text-sm text-muted-foreground truncate">{c.objective}</p>
              </div>
              <div className="text-right shrink-0">
                <p className="font-heading font-black text-xl">{formatMoney(c.budget, c.currency)}</p>
                <p className="text-sm text-muted-foreground flex items-center gap-1 justify-end"><Gavel className="h-3.5 w-3.5" /> {c.bids_count} bids</p>
              </div>
              <ArrowRight className="h-5 w-5 text-muted-foreground group-hover:text-neon-lime group-hover:translate-x-1 transition-all shrink-0" />
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}

function InfluencerDashboard({ user }) {
  const [stats, setStats] = useState(null);
  const [bids, setBids] = useState([]);

  useEffect(() => {
    api.get("/stats/influencer").then((r) => setStats(r.data));
    api.get("/bids/mine").then((r) => setBids(r.data));
  }, []);

  return (
    <div className="max-w-6xl mx-auto">
      <div className="mb-8">
        <p className="text-xs font-bold uppercase tracking-[0.2em] text-neon-pink mb-2">Creator dashboard</p>
        <h1 className="font-heading font-black text-4xl tracking-tight">Hi {user.name?.split(" ")[0]} ✨</h1>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-10">
        <StatCard icon={Gavel} label="Bids submitted" value={stats?.bids_submitted ?? "—"} i={0} />
        <StatCard icon={Clock} label="Pending" value={stats?.pending_bids ?? "—"} accent="neon-cyan" i={1} />
        <StatCard icon={Trophy} label="Won campaigns" value={stats?.accepted_bids ?? "—"} accent="neon-pink" i={2} />
        <StatCard icon={TrendingUp} label="Net earnings" value={stats ? formatMoney(stats.net_earnings, stats.currency) : "—"} accent="neon-purple" i={3} />
      </div>

      {stats && stats.accepted_bids > 0 && (
        <div className="rounded-2xl border border-neon-lime/20 bg-neon-lime/5 p-5 mb-8 text-sm text-muted-foreground">
          Net earnings are shown after the platform's <span className="text-neon-lime font-semibold">20% processing fee</span>. Gross won: {formatMoney(stats.gross_earnings, stats.currency)}.
        </div>
      )}

      <div className="flex items-center justify-between mb-5">
        <h2 className="font-heading font-bold text-2xl">Your bids</h2>
        <Link to="/discover"><Button data-testid="discover-btn" className="rounded-full bg-neon-pink text-white hover:bg-neon-pink/90 font-semibold"><Compass className="h-4 w-4 mr-1" /> Discover Campaigns</Button></Link>
      </div>

      {bids.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-border bg-surface p-12 text-center">
          <Compass className="h-10 w-10 text-muted-foreground mx-auto mb-4" />
          <p className="font-heading font-bold text-xl mb-2">No bids yet</p>
          <p className="text-muted-foreground mb-6">Browse open campaigns and submit your first proposal.</p>
          <Link to="/discover"><Button className="rounded-full bg-neon-pink text-white hover:bg-neon-pink/90 font-semibold">Discover campaigns</Button></Link>
        </div>
      ) : (
        <div className="grid gap-4">
          {bids.map((b) => (
            <Link key={b.bid_id} to={`/campaigns/${b.campaign_id}`} data-testid={`bid-card-${b.bid_id}`}
              className="group rounded-2xl border border-border bg-surface p-6 hover:bg-surfaceHover transition-colors flex items-center justify-between gap-4">
              <div className="min-w-0">
                <Badge variant="outline" className={`capitalize mb-1.5 ${statusColors[b.status]}`}>{b.status}</Badge>
                <h3 className="font-heading font-bold text-lg truncate group-hover:text-neon-pink transition-colors">{b.campaign_title}</h3>
                <p className="text-sm text-muted-foreground truncate">{b.deliverables}</p>
              </div>
              <div className="text-right shrink-0">
                <p className="font-heading font-black text-xl">{formatMoney(b.amount, b.currency)}</p>
                <p className="text-xs text-muted-foreground">your bid</p>
              </div>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}

export default function Dashboard() {
  const { user } = useAuth();
  return (
    <AppShell>
      {user?.role === "promoter" ? <PromoterDashboard user={user} /> : <InfluencerDashboard user={user} />}
    </AppShell>
  );
}
